import React from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { ArrowLeft, ClipboardCheck, Download, AlertCircle, CheckCircle } from 'lucide-react';
import DownloadApp from '@/components/shared/DownloadApp';
import { getFullUrl } from "@/lib/utils";

const DocumentCollection = () => {
  const documentCategories = [
    {
      title: "License Disc Renewal",
      nearMeSlug: "licence-renewals",
      documents: [
        "Valid ID document",
        "Current vehicle registration certificate",
        "Proof of address (not older than 3 months)",
        "Valid roadworthy certificate (if applicable)"
      ]
    },
    {
      title: "Vehicle Registration",
      nearMeSlug: "vehicle-registration",
      documents: [
        "Valid ID document",
        "Proof of ownership (invoice/agreement)",
        "Roadworthy certificate",
        "Proof of address",
        "Import permit (if imported vehicle)"
      ]
    },
    {
      title: "Change of Ownership",
      nearMeSlug: "change-of-ownership",
      documents: [
        "NCO form (completed)",
        "Valid ID documents (buyer & seller)",
        "Vehicle registration certificate",
        "Proof of address (buyer)",
        "Roadworthy certificate (if required)"
      ]
    }
  ];

  const collectionMethods = [
    {
      title: "Secure Collection Service",
      description: "We collect documents from your location",
      features: ["Door-to-door service", "Secure transport", "Receipt provided"]
    },
    {
      title: "Drop-off Points",
      description: "Multiple convenient locations",
      features: ["Extended hours", "Secure storage", "Same-day processing"]
    },
    {
      title: "Digital Upload",
      description: "Scan and upload documents securely",
      features: ["Instant verification", "24/7 availability", "Encrypted transfer"]
    }
  ];

  return (
    <>
      <Head>
        <title>Document Collection - We Queue For You | EzyFind Car Spares</title>
        <meta name="description" content="Document collection and verification for vehicle licensing services. Secure and convenient options available." />
      </Head>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-blue-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link href="/we-queue-for-you" className="inline-flex items-center gap-2 text-blue-200 hover:text-white mb-4">
              <ArrowLeft className="w-5 h-5" />
              Back to We Queue For You
            </Link>
            <h1 className="text-3xl lg:text-4xl font-bold">Document Collection</h1>
            <p className="text-xl text-blue-100 mt-2">Secure document handling and verification</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Document Requirements */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Required Documents by Service</h2>
            
            <div className="grid lg:grid-cols-3 gap-6">
              {documentCategories.map((category, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">{category.title}</h3>
                  <Link
                    href={`/queue-for-you-near-me/${category.nearMeSlug}`}
                    className="inline-block text-xs text-blue-600 hover:text-blue-800 font-medium mb-4"
                  >
                    Find {category.title} agents near me &rsaquo;
                  </Link>
                  <ul className="space-y-3">
                    {category.documents.map((doc, docIndex) => (
                      <li key={docIndex} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{doc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-yellow-800 mb-2">Important Notes</h4>
                  <ul className="text-yellow-700 space-y-1 text-sm">
                    <li>• All documents must be original or certified copies</li>
                    <li>• Proof of address must not be older than 3 months</li>
                    <li>• Foreign documents may require translation</li>
                    <li>• We verify all documents before processing</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Collection Methods */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Collection Methods</h2>
            
            <div className="grid lg:grid-cols-3 gap-6">
              {collectionMethods.map((method, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <ClipboardCheck className="w-8 h-8 text-blue-600" />
                    <h3 className="text-xl font-semibold text-gray-900">{method.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-4">{method.description}</p>
                  <ul className="space-y-2">
                    {method.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

        
          {/* Download Documents */}
          <section>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Download Forms & Documents</h2>
              <p className="text-gray-600 mb-8">Download all the forms and documents you need for your vehicle licensing services</p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Application Forms */}
                <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Declaration Letter</h3>
                  <p className="text-gray-600 text-sm mb-4">Declaration Letter - Informing Queue For You Services to collect your license on your behalf</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Declaration-Letter.pdf")}
                    download
                    className = "inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                </div>

                <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form ABR - Application and Notice in respect of Business Register Number</h3>
                  <p className="text-gray-600 text-sm mb-4">Registering a vehicle under the business</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-ABR-Application-and-Notice-in-respect-of-Business-Register-Number.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                </div>

                <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form ADV - Application for Deregistration of Vehicle</h3>
                  <p className="text-gray-600 text-sm mb-4">Deregistration of a Vehicle</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-ADV-Application-for-Deregistration-of-Vehicle.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                </div>

                <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form ALV - Application for Licensing of Motor Vehicle</h3>
                  <p className="text-gray-600 text-sm mb-4">Licensing of a Motor Vehicle</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-ALV-Application-for-Licensing-of-Motor-Vehicle.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                </div>

                <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form ANR - Application and Notice in respect of Traffic Register Number</h3>
                  <p className="text-gray-600 text-sm mb-4">Notice in respect of Traffic Register Number</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-ANR-Application-and-Notice-in-respect-of-Traffic-Register-Number.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                </div>

                <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form APD - Application for Particulars of Paper Card Type Driving license</h3>
                  <p className="text-gray-600 text-sm mb-4">Paper Card Type Driving license</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-APD-Application-for-Particulars-of-Paper-Card-Type-Driving-license.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                </div>

                <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form DL1 - Application for Driving license</h3>
                  <p className="text-gray-600 text-sm mb-4">Application for Driving license</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-DL1-Application-for-Driving-license.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                </div>

                <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form DLC - Application for Duplicate Registration or Deregistration Certificate in respect of Motor Vehicle</h3>
                  <p className="text-gray-600 text-sm mb-4">Duplicate Registration or Deregistration Certificate</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-DLC-Application-for-Duplicate-Registration-or-Deregistration-Certificate-in-respect-of-Motor-Vehicle.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                </div>

                <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form ELF1 - Application for Special Classification in respect of license Fees</h3>
                  <p className="text-gray-600 text-sm mb-4">Special license Fees</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-ELF1-Application-for-Special-Classification-in-respect-of-license-Fees.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                </div>

                <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form MIB - Application and Notice in respect of Registration of a Manufacturer or Importer or Builder of Vehicles</h3>
                  <p className="text-gray-600 text-sm mb-4">Registration of a Manufacturer or Importer or Builder</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-MIB-Application-and-Notice-in-respect-of-Registration-of-a-Manufacturer-or-Importer-or-Builder-of-Vehicles.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                </div>

                <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form MTN1 - Application and Notice in respect of Motor Trade Number and Blank Temporary Permits</h3>
                  <p className="text-gray-600 text-sm mb-4">Blank Temporary Permits</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-MTN1-Application-and-Notice-in-respect-of-Motor-Trade-Number-and-Blank-Temporary-Permits.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                </div>

                <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form MVR1-MIB - Application for Initial Registration of a Motor Vehicle</h3>
                  <p className="text-gray-600 text-sm mb-4">Initial Registration of a Motor Vehicle</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-MVR1-MIB-Application-for-Initial-Registration-of-a-Motor-Vehicle.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form MVR2 - Application in respect of license Number for Motor Vehicle</h3>
                  <p className="text-gray-600 text-sm mb-4">license Number for Motor Vehicle</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-MVR2-Application-in-respect-of-license-Number-for-Motor-Vehicle.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                   </div>
                              
                  <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form NCO - Notification of Change of Ownership or Sale of Motor Vehicle</h3>
                  <p className="text-gray-600 text-sm mb-4">Change of ownership</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-NCO-Notification-of-Change-of-Ownership-or-Sale-of-Motor-Vehicle.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                              </div>
                               <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form NCP - Notification of Change of Address or Particulars of Person or Organisation</h3>
                  <p className="text-gray-600 text-sm mb-4">Change of address</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-NCP-Notification-of-Change-of-Address-or-Particulars-of-Person-or-Organisation.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                              </div>
                               <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form PD1 - Application for Professional Driving Permit</h3>
                  <p className="text-gray-600 text-sm mb-4">Driving Permit</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-PD1-Application-for-Professional-Driving-Permit.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                              </div>
                               <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form RLF - Application for Refund of License Fees</h3>
                  <p className="text-gray-600 text-sm mb-4">Refund license fees</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-RLF-Application-for-Refund-of-License-Fees.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                              </div>
                               <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form RLV - Application for Registration and Licensing of Motor Vehicle</h3>
                  <p className="text-gray-600 text-sm mb-4">Registration of vehicle</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-RLV-Application-for-Registration-and-Licensing-of-Motor-Vehicle.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                              </div>
                               <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Form TSP1 - Application for Temporary or Special Permit</h3>
                  <p className="text-gray-600 text-sm mb-4">Temporary permit</p>
                  <a
                    href={getFullUrl("Documents/queue-for-you/Form-TSP1-Application-for-Temporary-or-Special-Permit.pdf")}
                    download
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                </div>
              </div>

              {/* Informational Documents */}
              {/* <div className="mt-12">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Information & Guidelines</h3>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <h4 className="font-semibold text-gray-900 mb-2">Document Checklist</h4>
                    <p className="text-gray-600 text-sm mb-4">Complete requirements for all services</p>
                    <a
                      href={getFullUrl("Documents/queue-for-you/vehicle-requirements-checklist.pdf")}
                      download
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                    >
                      <Download className="w-4 h-4" />
                      Download PDF
                    </a>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <h4 className="font-semibold text-gray-900 mb-2">Pricing Guide</h4>
                    <p className="text-gray-600 text-sm mb-4">Current pricing for all services</p>
                    <a
                      href={getFullUrl("Documents/queue-for-you/vehicle-services-pricing-guide.pdf")}
                      download
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                    >
                      <Download className="w-4 h-4" />
                      Download PDF
                    </a>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <h4 className="font-semibold text-gray-900 mb-2">Terms of Service</h4>
                    <p className="text-gray-600 text-sm mb-4">Service terms and conditions</p>
                    <a
                      href={getFullUrl("Documents/queue-for-you/terms-of-service.pdf")}
                      download
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                    >
                      <Download className="w-4 h-4" />
                      Download PDF
                    </a>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <h4 className="font-semibold text-gray-900 mb-2">Privacy Policy</h4>
                    <p className="text-gray-600 text-sm mb-4">Data protection and privacy</p>
                    <a
                      href={getFullUrl("Documents/queue-for-you/privacy-policy.pdf")}
                      download
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                    >
                      <Download className="w-4 h-4" />
                      Download PDF
                    </a>
                  </div>
                </div>
              </div> */}

              {/* Help Section */}
              <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">Need Help?</h4>
                    <p className="text-blue-700 text-sm mb-3">
                      If you're unsure which forms you need or have questions about completing them, 
                      our team is here to help. Submit live real-time chat enquiry on the website or mobile app for Android, iPhone or Huawei.
                    </p>
                    
                  </div>
                </div>
              </div>
            </div>
          </section>
          <DownloadApp/>
        </div>
      </div>
    </>
  );
};

export default DocumentCollection;